import { getLevelAmount } from "../../config/autoFillProgramConfig";
import {
  AUTO_FILL_FORFEITURE_LEVEL_MAX,
  levelUnwithdrawnAmount,
  resolveForfeitureLevelMin,
} from "../../config/autoFillLevelForfeitureConfig";

export function initialLevelForfeitureState(reachedAtIso) {
  return {
    reachedAt: reachedAtIso,
    pointsCredited: 0,
    pointsWithdrawn: 0,
    pointsForfeited: 0,
    forfeitureProcessedAt: null,
  };
}

export function getLevelForfeitureState(program, level) {
  const L = Number(level);
  const map = program?.levelForfeiture || {};
  const fromMap = map[String(L)] || map[L] || null;
  if (L === 6 && program?.level6) {
    return { ...program.level6, ...(fromMap || {}) };
  }
  return fromMap;
}

export function buildLevelForfeitureFirestorePatch(program, level, nextState) {
  const L = Number(level);
  const patch = {
    levelForfeiture: {
      ...(program?.levelForfeiture || {}),
      [String(L)]: nextState,
    },
    updatedAt: new Date().toISOString(),
  };
  if (L === 6) patch.level6 = nextState;
  return patch;
}

export function sumPointsCreditedFromLedgerRows(programId, userId, level, ledgerRows) {
  const L = Number(level);
  return (ledgerRows || []).reduce((sum, row) => {
    if (row.programId !== programId || row.userId !== userId) return sum;
    if (Number(row.level) !== L) return sum;
    if (row.transactionType !== "POINTS") return sum;
    const amt = Number(row.amount) || 0;
    if (row.fromWallet === "POINTS" && row.toWallet === "ADJUSTMENT") {
      return sum - amt;
    }
    if (row.toWallet && row.toWallet !== "POINTS") return sum;
    return sum + amt;
  }, 0);
}

/** CO tranche at level L only after this program's L matrix has ≥1 fill (pay #1 landed). */
export function cappedCoPointsEarnedAtLevel(
  programId,
  userId,
  level,
  ledgerRows,
  filledMatrixSlotsAtLevel
) {
  if (Number(filledMatrixSlotsAtLevel) === 0) {
    return 0;
  }
  const raw = sumPointsCreditedFromLedgerRows(
    programId,
    userId,
    level,
    ledgerRows
  );
  return Math.min(getLevelAmount(level), raw);
}

export function hydrateLevelForfeitureStates(program, ledgerRows = []) {
  const currentLevel = Number(program?.currentLevel) || 1;
  const minLevel = resolveForfeitureLevelMin(program);
  const maxLevel = Math.min(currentLevel, AUTO_FILL_FORFEITURE_LEVEL_MAX);
  if (maxLevel < minLevel) {
    return { ...program };
  }

  const programCopy = {
    ...program,
    levelForfeiture: { ...(program?.levelForfeiture || {}) },
  };

  for (let L = minLevel; L <= maxLevel; L += 1) {
    let state = getLevelForfeitureState(programCopy, L);
    if (!state?.reachedAt) {
      const reachedAt =
        L === 6 && program.level6?.reachedAt
          ? program.level6.reachedAt
          : new Date().toISOString();
      state = initialLevelForfeitureState(reachedAt);
    }
    const fromLedger = sumPointsCreditedFromLedgerRows(
      program.programId,
      program.userId,
      L,
      ledgerRows
    );
    const cap = getLevelAmount(L);
    const credited = Math.min(
      cap,
      Math.max(Number(state.pointsCredited) || 0, fromLedger)
    );
    const nextState = { ...state, pointsCredited: credited };
    const partial = buildLevelForfeitureFirestorePatch(programCopy, L, nextState);
    programCopy.levelForfeiture = partial.levelForfeiture;
    if (partial.level6) programCopy.level6 = partial.level6;
  }

  return programCopy;
}

export function mergeLevelForfeiturePersistUpdates(
  programBefore,
  programHydrated,
  levelWithdrawUpdates,
  currentLevel
) {
  const minLevel = resolveForfeitureLevelMin(programBefore);
  const maxLevel = Math.min(
    Number(currentLevel) || 1,
    AUTO_FILL_FORFEITURE_LEVEL_MAX
  );
  const merged = {};
  for (let L = minLevel; L <= maxLevel; L += 1) {
    const next =
      levelWithdrawUpdates[L] || getLevelForfeitureState(programHydrated, L);
    const prev = getLevelForfeitureState(programBefore, L);
    if (!next) continue;
    const changed =
      !prev ||
      prev.reachedAt !== next.reachedAt ||
      Number(prev.pointsCredited) !== Number(next.pointsCredited) ||
      Number(prev.pointsWithdrawn) !== Number(next.pointsWithdrawn);
    if (changed) merged[L] = next;
  }
  return merged;
}

export function allocateWithdrawalAcrossLevelStates(program, currentLevel, withdrawalAmount) {
  const minLevel = resolveForfeitureLevelMin(program);
  const maxLevel = Math.min(
    Number(currentLevel) || 1,
    AUTO_FILL_FORFEITURE_LEVEL_MAX
  );
  let remaining = Number(withdrawalAmount) || 0;
  const levelUpdates = {};
  const appliedByLevel = {};

  for (let L = minLevel; L <= maxLevel; L += 1) {
    const state = getLevelForfeitureState(program, L);
    if (!state) continue;
    const outstanding = levelUnwithdrawnAmount(state);
    if (outstanding <= 0 || remaining <= 0) continue;
    const applied = Math.min(remaining, outstanding);
    levelUpdates[L] = {
      ...state,
      pointsWithdrawn: (Number(state.pointsWithdrawn) || 0) + applied,
    };
    appliedByLevel[L] = applied;
    remaining -= applied;
  }

  return { levelUpdates, appliedByLevel, remaining };
}

/** Apply a full tranche withdrawal to one matrix level (per-level withdrawal requests). */
export function allocateWithdrawalAtSingleLevel(program, matrixLevel, withdrawalAmount) {
  const L = Number(matrixLevel);
  const state = getLevelForfeitureState(program, L);
  const amount = Number(withdrawalAmount) || 0;
  if (!state || amount <= 0) {
    return { levelUpdates: {}, appliedByLevel: {}, remaining: amount };
  }
  const outstanding = levelUnwithdrawnAmount(state);
  const applied = Math.min(amount, outstanding);
  if (applied <= 0) {
    return { levelUpdates: {}, appliedByLevel: {}, remaining: amount };
  }
  return {
    levelUpdates: {
      [L]: {
        ...state,
        pointsWithdrawn: (Number(state.pointsWithdrawn) || 0) + applied,
      },
    },
    appliedByLevel: { [L]: applied },
    remaining: amount - applied,
  };
}
